import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Send, Video, BarChart3, MessageCircle, Play, Download, Copy, Eye,
  VolumeX, Clock, Film, Zap, Target, Users, Globe, Languages, Award,
  ExternalLink, AlertTriangle, CheckCircle, RefreshCw, Settings,
  FileText, Database, Hash, TrendingUp, Activity, ChevronDown, Book, Trash2
} from 'lucide-react';
import messageLogo from './assets/message-logo.png';
import PDFExport from './components/PDFExport';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

const VideomagicPromo = () => {
  const { sessionId: urlSessionId } = useParams();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('chat');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [movieName, setMovieName] = useState('');
  const [currentMovieUrl, setCurrentMovieUrl] = useState('');
  const [movieAnalysis, setMovieAnalysis] = useState(null);
  const [selectedScenes, setSelectedScenes] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [previousChats, setPreviousChats] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [showModal, setShowModal] = useState(!urlSessionId);
  const [modalActiveTab, setModalActiveTab] = useState('addMovie');
  const [newMovieUrl, setNewMovieUrl] = useState('');
  const [newMovieName, setNewMovieName] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [availableMovies, setAvailableMovies] = useState([]);
  const [modalPreviousChats, setModalPreviousChats] = useState([]);
  const [isLoadingMovies, setIsLoadingMovies] = useState(false);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [isAddingMovie, setIsAddingMovie] = useState(false);
  const [addMovieSuccess, setAddMovieSuccess] = useState(false);
  const [expandedPrompts, setExpandedPrompts] = useState({});
  const [deletingChatId, setDeletingChatId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const lastPromptRef = useRef(null);

  //https://c3ived5vhr5ul6mv3xid5jseke0tqbzw.lambda-url.us-east-1.on.aws Dev
  //https://ujkdnyhmaivig52vddy7koa77y0xvrhs.lambda-url.us-east-1.on.aws Prod

  const API_BASE = process.env.REACT_APP_API_BASE || 'https://c3ived5vhr5ul6mv3xid5jseke0tqbzw.lambda-url.us-east-1.on.aws';
  const INGEST_API = 'https://luumfs5o7oqrj5tshqosfzjike0ymdez.lambda-url.us-east-1.on.aws/'

  const formatRelativeTime = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) return 'now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const validateAddMovieForm = () => {
    const errors = {};
    
    if (!newMovieName.trim()) {
      errors.movieName = 'Movie name is required';
    } else if (newMovieName.trim().length < 2) {
      errors.movieName = 'Movie name must be at least 2 characters long';
    }
    
    if (!newMovieUrl.trim()) {
      errors.movieUrl = 'Movie URL is required';
    } else {
      try {
        const url = new URL(newMovieUrl);
        if (!['http:', 'https:'].includes(url.protocol)) {
          errors.movieUrl = 'URL must start with http:// or https://';
        }
      } catch {
        errors.movieUrl = 'Please enter a valid URL';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const togglePromptExpansion = (promptIndex) => {
    setExpandedPrompts(prev => ({
      ...prev,
      [promptIndex]: !prev[promptIndex]
    }));
  };

  const handleDeleteChat = (chat) => {
    setChatToDelete(chat);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteChat = async () => {
    if (!chatToDelete) return;
    
    try {
      setDeletingChatId(chatToDelete.sessionId);
      const response = await fetch(`${API_BASE}/api/chat/${chatToDelete.sessionId}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Remove the deleted chat from the list
        setModalPreviousChats(prev => 
          prev.filter(chat => chat.sessionId !== chatToDelete.sessionId)
        );
        setPreviousChats(prev => 
          prev.filter(chat => chat.sessionId !== chatToDelete.sessionId)
        );
        
        if (data.success) {
          setDeleteSuccess(true);
          // Hide success message after 3 seconds
          setTimeout(() => {
            setDeleteSuccess(false);
          }, 3000);
        }
      } else {
        console.error('Failed to delete chat');
        // You could add error handling here
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
    } finally {
      setDeletingChatId(null);
      setShowDeleteConfirm(false);
      setChatToDelete(null);
    }
  };

  const cancelDeleteChat = () => {
    setShowDeleteConfirm(false);
    setChatToDelete(null);
  };

  const handleAddMovie = async () => {
    if (!validateAddMovieForm()) return;
    
    try {
      setIsAddingMovie(true);
      const payload = {
        video_url: newMovieUrl.trim(),
        movie_name: newMovieName.trim(),
        movie_id: "527f1f77bcf86cd799439011",
        callback_url: `${API_BASE}/api/movies/ingest`
      };
      
      const response = await fetch(`${INGEST_API}process-content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (response.ok && data.status === 'completed') {
        setAddMovieSuccess(true);
        setNewMovieName('');
        setNewMovieUrl('');
        setValidationErrors({});
        
        setTimeout(() => {
          setAddMovieSuccess(false);
          setShowModal(false);
        }, 3000);
      } else {
        setValidationErrors({ 
          general: data.message || 'Failed to start movie processing. Please try again.' 
        });
      }
    } catch (error) {
      console.error('Error adding movie:', error);
      setValidationErrors({ 
        general: 'Network error occurred. Please check your connection and try again.' 
      });
    } finally {
      setIsAddingMovie(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (activeTab === 'clips' && prompts.length > 0) {
      // Expand the last accordion
      const lastPromptIndex = prompts.length - 1;
      setExpandedPrompts(prev => ({
        ...prev,
        [lastPromptIndex]: true
      }));
      
      // Scroll to the last accordion after a short delay to ensure DOM is updated
      setTimeout(() => {
        lastPromptRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [activeTab, prompts.length]);

  useEffect(() => {
    if (!urlSessionId) {
      testConnection();
    }
    if (showModal) {
      loadAvailableMovies();
      loadModalPreviousChats();
    }
  }, [showModal, urlSessionId]);

  useEffect(() => {
    if (urlSessionId) {
      setIsLoading(true);
      loadChatSession(urlSessionId);
    }
  }, [urlSessionId]);

  const testConnection = async () => {
    try {
      const response = await fetch(`${API_BASE}/health`);
      setConnectionStatus(response.ok ? 'connected' : 'disconnected');
    } catch {
      setConnectionStatus('disconnected');
    }
  };

  const loadPreviousChats = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/chat/user/chats`);
      if (response.ok) {
        const data = await response.json();
        setPreviousChats(data.chats || []);
      }
    } catch (error) {
      console.error('Error loading chats:', error);
    }
  };

  const loadAvailableMovies = async () => {
    try {
      setIsLoadingMovies(true);
      const response = await fetch(`${API_BASE}/api/movies`);
      if (response.ok) {
        const data = await response.json();
        setAvailableMovies(data.movies || []);
      }
    } catch (error) {
      console.error('Error loading movies:', error);
    } finally {
      setIsLoadingMovies(false);
    }
  };

  const loadModalPreviousChats = async () => {
    try {
      setIsLoadingChats(true);
      const response = await fetch(`${API_BASE}/api/chat/user/chats`);
      if (response.ok) {
        const data = await response.json();
        setModalPreviousChats(data.chats || []);
        setPreviousChats(data.chats || []);
      }
    } catch (error) {
      console.error('Error loading modal chats:', error);
    } finally {
      setIsLoadingChats(false);
    }
  };

  const startChat = async (name, videoUrl) => {
    try {
      setIsLoading(true);
      const payload = { movieName: name };
      if (videoUrl) payload.videoUrl = videoUrl;
      
      const response = await fetch(`${API_BASE}/api/chat/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        setSessionId(data.sessionId);
        setMovieName(data.movieName);
        setCurrentMovieUrl(data.movie_info?.videoUrl);
        setMessages([{
          role: 'assistant',
          content: data.message || `Session started for "${data.movieName}". Ready to analyze and create trailer clips.`,
          timestamp: new Date()
        }]);
        navigate(`/chat/${data.sessionId}`);
      }
    } catch (error) {
      console.error('Error starting chat:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    if (!sessionId) {
      const parts = input.trim().split(' | ');
      await startChat(parts[0], parts[1]);
      setInput('');
      return;
    }

    const userMsg = { role: 'user', content: input.trim(), timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    
    const inputValue = input;
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/chat/${sessionId}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMsg.content })
      });

      if (!response.ok) throw new Error('Failed to send message');
      const data = await response.json();
      
      console.log('API Response:', data);
      
      if (data.state) {
        console.log('Updating state with:', data.state);
        if (data.state.movie_analysis) {
          setMovieAnalysis(data.state.movie_analysis);
        }
        if (data.state.prompts) {
          setPrompts(data.state.prompts);
          const scenes = data.state.prompts.flatMap(p => p.scenes || []);
          console.log('Setting scenes:', scenes);
          setSelectedScenes(scenes);
        }
      }

      const lastMsg = data.messages[data.messages.length - 1];
      const assistantMsg = {
        role: 'assistant',
        content: lastMsg.content,
        timestamp: new Date(),
        messageType: lastMsg.additional_kwargs?.message_type,
        scenesData: lastMsg.additional_kwargs?.scenes_data,
        analysisData: lastMsg.additional_kwargs?.analysis_data
      };

      setMessages(prev => [...prev, assistantMsg]);

      if (assistantMsg.messageType === 'scene_selection') {
        setTimeout(() => setActiveTab('clips'), 500);
      } else if (assistantMsg.messageType === 'movie_analysis') {
        setTimeout(() => setActiveTab('analysis'), 500);
      }

    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Error processing request. Please try again.',
        timestamp: new Date(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, sessionId, API_BASE]);

  const loadChatSession = async (chatSessionId) => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE}/api/chat/${chatSessionId}`);
      if (response.ok) {
        const data = await response.json();
        console.log('Loading chat session data:', data);
        
        setSessionId(chatSessionId);
        setMovieName(data.movieName || 'Movie');
        setCurrentMovieUrl(data.videoUrl || 'NO URL')
        setMessages(data.chat.messages || []);

        if (data.chat.agent_state) {
          console.log('Loading state:', data.chat.agent_state);
          if (data.chat.agent_state.movie_analysis) setMovieAnalysis(data.chat.agent_state.movie_analysis);
          if (data.chat.agent_state.prompts) {
            setPrompts(data.chat.agent_state.prompts);
            const scenes = data.chat.agent_state.prompts.flatMap(p => p.scenes || []);
            setSelectedScenes(scenes);
          }
        }
        
        if (!urlSessionId) {
          navigate(`/chat/${chatSessionId}`);
        }
      }
    } catch (error) {
      console.error('Error loading chat:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetSession = () => {
    setSessionId('');
    setMovieName('');
    setMessages([]);
    setMovieAnalysis(null);
    setSelectedScenes([]);
    setPrompts([]);
    setActiveTab('chat');
    setInput('');
    setShowModal(true);
    navigate('/');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Startup Modal */}
      {showModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Welcome to Videomagic Promo</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body p-0">
                {/* Modal Tabs */}
                <div className="border-bottom">
                  <ul className="nav nav-pills nav-justified p-3">
                    <li className="nav-item">
                      <button
                        className={`nav-link ${modalActiveTab === 'addMovie' ? 'active' : ''}`}
                        onClick={() => setModalActiveTab('addMovie')}
                      >
                        Add New Movie
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${modalActiveTab === 'availableMovies' ? 'active' : ''}`}
                        onClick={() => setModalActiveTab('availableMovies')}
                      >
                        Available Movies
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${modalActiveTab === 'previousChats' ? 'active' : ''}`}
                        onClick={() => setModalActiveTab('previousChats')}
                      >
                        Previous Chats
                      </button>
                    </li>
                  </ul>
                </div>

                {/* Add New Movie Tab */}
                {modalActiveTab === 'addMovie' && (
                  <div className="p-4" style={{minHeight: '60vh'}}>
                    <div className="row justify-content-center h-100">
                      <div className="col-md-8 d-flex align-items-center">
                        <div className="w-100">
                          <div className="text-center mb-4">
                            <Video size={60} className="text-primary mb-3" />
                            <h4 className="fw-bold">Add New Movie</h4>
                            <p className="text-muted">Enter movie details to add a new movie</p>
                          </div>

                          {addMovieSuccess && (
                            <div className="alert alert-success text-center mb-4">
                              <CheckCircle size={48} className="text-success mb-3" />
                              <h5 className="fw-bold">Ingestion has been started!</h5>
                              <p className="mb-2">Please wait a couple of minutes for processing to complete.</p>
                              <small className="text-muted">
                                Waiting time can depend on video duration and quality.
                              </small>
                            </div>
                          )}

                          {validationErrors.general && (
                            <div className="alert alert-danger mb-4">
                              <AlertTriangle size={20} className="me-2" />
                              {validationErrors.general}
                            </div>
                          )}
                          
                          <div className="mb-3">
                            <label className="form-label fw-semibold">Movie Name *</label>
                            <input
                              type="text"
                              className={`form-control form-control-lg ${validationErrors.movieName ? 'is-invalid' : ''}`}
                              placeholder="Enter movie name"
                              value={newMovieName}
                              onChange={(e) => {
                                setNewMovieName(e.target.value);
                                if (validationErrors.movieName) {
                                  setValidationErrors(prev => ({ ...prev, movieName: '' }));
                                }
                              }}
                              disabled={isAddingMovie || addMovieSuccess}
                            />
                            {validationErrors.movieName && (
                              <div className="invalid-feedback">
                                {validationErrors.movieName}
                              </div>
                            )}
                          </div>

                          <div className="mb-4">
                            <label className="form-label fw-semibold">Video URL *</label>
                            <input
                              type="url"
                              className={`form-control form-control-lg ${validationErrors.movieUrl ? 'is-invalid' : ''}`}
                              placeholder="https://example.com/video.mp4"
                              value={newMovieUrl}
                              onChange={(e) => {
                                setNewMovieUrl(e.target.value);
                                if (validationErrors.movieUrl) {
                                  setValidationErrors(prev => ({ ...prev, movieUrl: '' }));
                                }
                              }}
                              disabled={isAddingMovie || addMovieSuccess}
                            />
                            {validationErrors.movieUrl && (
                              <div className="invalid-feedback">
                                {validationErrors.movieUrl}
                              </div>
                            )}
                          </div>

                          <div className="d-grid">
                            <button 
                              className="btn btn-primary btn-lg"
                              onClick={handleAddMovie}
                              disabled={isAddingMovie || addMovieSuccess}
                            >
                              {isAddingMovie ? (
                                <>
                                  <div className="spinner-border spinner-border-sm me-2"></div>
                                  Processing...
                                </>
                              ) : addMovieSuccess ? (
                                <>
                                  <CheckCircle size={20} className="me-2" />
                                  Success!
                                </>
                              ) : (
                                'Add Movie'
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Available Movies Tab */}
                {modalActiveTab === 'availableMovies' && (
                  <div className="p-4" style={{minHeight: '60vh', maxHeight: '60vh', overflowY: 'auto'}}>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h4 className="fw-bold">Available Movies</h4>
                      <button 
                        className="btn btn-outline-primary"
                        onClick={loadAvailableMovies}
                        disabled={isLoadingMovies}
                      >
                        {isLoadingMovies ? (
                          <div className="spinner-border spinner-border-sm me-2"></div>
                        ) : (
                          <RefreshCw size={16} className="me-2" />
                        )}
                        Refresh
                      </button>
                    </div>
                    
                    {isLoadingMovies ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary"></div>
                        <p className="mt-3 text-muted">Loading movies...</p>
                      </div>
                    ) : availableMovies.length === 0 ? (
                      <div className="text-center py-5">
                        <Film size={60} className="text-muted mb-3" />
                        <h5>No Movies Available</h5>
                        <p className="text-muted">Add some movies to get started</p>
                      </div>
                    ) : (
                      <div className="row g-3">
                        {availableMovies.map((movie) => (
                          <div key={movie._id} className="col-md-6 col-lg-4">
                            <div className="card h-100 shadow-sm">
                              <div className="card-body">
                                <h6
                                  className="card-title fw-bold text-truncate"
                                  title={movie.movieName}
                                >
                                  {movie.movieName}
                                </h6>
                                <div className="small text-muted mb-2">
                                  <div>Status: <span className={`badge ${movie.processing_status === 'completed' ? 'bg-success' : 'bg-warning'}`}>
                                    {movie.processing_status}
                                  </span></div>
                                </div>
                                {movie.metadata?.videoUrl && (
                                  <div className="mb-2">
                                    <div className="d-flex align-items-center gap-2">
                                      <small className="text-muted flex-grow-1" style={{minWidth: 0}}>
                                        URL: <a
                                          href={movie.metadata.videoUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary text-decoration-none"
                                          style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            display: 'inline-block',
                                            maxWidth: '200px',
                                            verticalAlign: 'bottom'
                                          }}
                                        >
                                          {movie.metadata.videoUrl}
                                        </a>
                                      </small>
                                      <button
                                        className="btn btn-sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigator.clipboard.writeText(movie.metadata.videoUrl);
                                        }}
                                        title="Copy URL"
                                        style={{border: 'none', padding: '4px 8px'}}
                                      >
                                        <Copy size={12} />
                                      </button>
                                    </div>
                                  </div>
                                )}
                                <button 
                                  className="btn btn-primary btn-sm w-100"
                                  onClick={() => {
                                    setShowModal(false);
                                    startChat(movie.movieName, movie.metadata?.videoUrl);
                                  }}
                                  disabled={movie.processing_status !== 'completed'}
                                >
                                  {movie.processing_status === 'completed' ? 'Start Chat' : 'Processing...'}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Previous Chats Tab */}
                {modalActiveTab === 'previousChats' && (
                  <div className="p-4" style={{minHeight: '60vh', maxHeight: '60vh', overflowY: 'auto'}}>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h4 className="fw-bold">Previous Chats</h4>
                      <button 
                        className="btn btn-outline-primary"
                        onClick={loadModalPreviousChats}
                        disabled={isLoadingChats}
                      >
                        {isLoadingChats ? (
                          <div className="spinner-border spinner-border-sm me-2"></div>
                        ) : (
                          <RefreshCw size={16} className="me-2" />
                        )}
                        Refresh
                      </button>
                    </div>
                    
                    {isLoadingChats ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary"></div>
                        <p className="mt-3 text-muted">Loading chats...</p>
                      </div>
                    ) : modalPreviousChats.length === 0 ? (
                      <div className="text-center py-5">
                        <MessageCircle size={60} className="text-muted mb-3" />
                        <h5>No Previous Chats</h5>
                        <p className="text-muted">Start a new chat to see it here</p>
                      </div>
                    ) : (
                      <div className="row g-3">
                        {modalPreviousChats.map((chat) => (
                          <div key={chat._id} className="col-md-6 col-lg-4">
                            <div className="card h-100 shadow-sm position-relative">
                              <button
                                className="btn btn-outline-danger btn-sm position-absolute"
                                style={{ top: '10px', right: '10px', zIndex: 1 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteChat(chat);
                                }}
                                disabled={deletingChatId === chat.sessionId}
                              >
                                {deletingChatId === chat.sessionId ? (
                                  <div className="spinner-border spinner-border-sm"></div>
                                ) : (
                                  <Trash2 size={14} />
                                )}
                              </button>
                              <div className="card-body" style={{paddingRight: '50px'}}>
                                <h6
                                  className="card-title fw-bold text-truncate"
                                  title={chat.movieName || `Session: ${chat.sessionId.split('-')[1]}`}
                                >
                                  {chat.movieName || `Session: ${chat.sessionId.split('-')[1]}`}
                                </h6>
                                <div className="small text-muted mb-3">
                                  {chat.movieName && (
                                    <div>Session: {chat.sessionId.split('-')[1]}</div>
                                  )}
                                  {!chat.movieName && (
                                    <div>Movie ID: {chat.movieId}</div>
                                  )}
                                  <div>Status: <span className={`badge ${chat.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                                    {chat.status}
                                  </span></div>
                                  <div>Messages: {chat.total_messages || 0}</div>
                                  <div>Last Activity: {formatRelativeTime(chat.last_activity)}</div>
                                </div>
                                <button 
                                  className="btn btn-primary btn-sm w-100"
                                  onClick={() => {
                                    setShowModal(false);
                                    loadChatSession(chat.sessionId);
                                  }}
                                >
                                  Continue Chat
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Delete Chat Session</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={cancelDeleteChat}
                ></button>
              </div>
              <div className="modal-body">
                <div className="text-center py-3">
                  <AlertTriangle size={60} className="text-warning mb-3" />
                  <h5 className="fw-bold mb-3">Are you sure?</h5>
                  <p className="text-muted mb-4">
                    This will permanently delete the chat session "{chatToDelete?.movieName || `Session: ${chatToDelete?.sessionId.split('-')[1]}`}" and all its messages.
                  </p>
                  <p className="text-muted small">
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={cancelDeleteChat}
                  disabled={deletingChatId}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={confirmDeleteChat}
                  disabled={deletingChatId}
                >
                  {deletingChatId ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} className="me-2" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {deleteSuccess && (
        <div 
          className="position-fixed top-0 end-0 m-4" 
          style={{zIndex: 9999}}
        >
          <div className="toast show" role="alert">
            <div className="toast-header bg-success text-white border-0">
              <CheckCircle size={20} className="me-2" />
              <strong className="me-auto">Success!</strong>
              <button 
                type="button" 
                className="btn-close btn-close-white" 
                onClick={() => setDeleteSuccess(false)}
              ></button>
            </div>
            <div className="toast-body">
              Chat session has been successfully deleted.
            </div>
          </div>
        </div>
      )}

      <div className="min-vh-100 d-flex flex-column" style={{backgroundColor: '#f8f9fa', height: '100vh', overflow: 'hidden'}}>
        {/* Header */}
      <div className="bg-white border-bottom shadow-sm">
        <div className="container-fluid">
          <div className="row align-items-center pt-3">
            
            <div className="col-md-8">
              <div
                className="d-flex align-items-center cursor-pointer"
                onClick={resetSession}
                style={{cursor: 'pointer'}}
              >
                <div className="text-white rounded p-3 me-3">
                  <img
                    src={messageLogo}
                    alt="Logo"
                    style={{ width: '28px', height: '28px' }}
                  />
                </div>
                <div style={{minWidth: 0, flex: 1}}>
  <h1 className="h3 mb-0 fw-bold">Videomagic Promo</h1>
  <p className="text-muted small mb-0">
    {movieName ? `Working on: ${movieName}` : 'AI-Powered Movie Trailer Creator'}
  </p>
  <div className="text-muted small mb-0" style={{minWidth: 0}}>
              {currentMovieUrl ? (
                <span className="d-flex align-items-center gap-2" style={{minWidth: 0}}>
                  <a
                    href={currentMovieUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary text-decoration-none"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                      display: 'inline-block',
                      minWidth: 0
                    }}
                  >
                    {currentMovieUrl}
                  </a>
                  <button
                    className="btn btn-sm flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(currentMovieUrl);
                    }}
                    title="Copy URL"
                    style={{border: 'none', padding: '4px 8px'}}
                  >
                    <Copy size={14} />
                  </button>
                </span>
              ) : (
                'NO URL'
              )}
            </div>
</div>
              </div>
            </div>
            <div className="col-md-4 text-end">
              <div className="d-flex align-items-center justify-content-end gap-3">
                <div className={`d-flex align-items-center gap-2 px-3 py-1 rounded-pill ${
                  connectionStatus === 'connected' ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'
                }`}>
                  <div className={`rounded-circle ${
                    connectionStatus === 'connected' ? 'bg-success' : 'bg-danger'
                  }`} style={{width: '8px', height: '8px'}}></div>
                  <span className="small fw-medium">
                    {connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
                  </span>
                </div>
                {sessionId && (
                  <button className="btn btn-outline-danger btn-sm" onClick={resetSession}>
                    End Session
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-grow-1 d-flex overflow-hidden">
        {/* Main Content */}
        <div className="flex-grow-1 d-flex flex-column">
          {/* Tab Navigation */}
          {sessionId && (
            <div className="bg-white border-bottom">
              <ul className="nav nav-pills nav-fill p-3">
                {[
                  { id: 'chat', label: 'Chat', icon: MessageCircle, count: messages.length },
                  { id: 'clips', label: 'Clips', icon: Video, count: selectedScenes.length },
                  { id: 'analysis', label: 'Analysis', icon: BarChart3, available: !!movieAnalysis }
                ].map(tab => (
                  <li key={tab.id} className="nav-item mx-1">
                    <button
                      className={`nav-link d-flex align-items-center py-3 px-4 rounded fw-semibold ${
                        activeTab === tab.id ? 'active bg-primary text-white' : 'text-muted'
                      }`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      <tab.icon size={18} className="me-2" />
                      {tab.label}
                      {tab.count > 0 && (
                        <span className={`badge ms-2 ${
                          activeTab === tab.id ? 'bg-white text-primary' : 'bg-primary'
                        }`}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Chat Tab */}
          {activeTab === 'chat' && (
            <div className="flex-grow-1 d-flex flex-column">
              {/* Messages Area - Fixed Height */}
              <div 
                className="overflow-auto p-4" 
                style={{
                  height: 'calc(100vh - 280px)', 
                  backgroundColor: '#fafbfc'
                }}
              >
                {isLoading && !sessionId ? (
                  <div className="text-center py-5 d-flex align-items-center justify-content-center" style={{minHeight: '400px'}}>
                    <div>
                      <div className="spinner-border text-primary mb-4" style={{width: '3rem', height: '3rem'}}></div>
                      <h4 className="fw-bold mb-2">Starting Chat Session</h4>
                      <p className="text-muted">Please wait while we initialize your movie session...</p>
                    </div>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-4" 
                         style={{width: '80px', height: '80px'}}>
                      <img 
                        src={messageLogo} 
                        alt="Logo" 
                        style={{ width: '40px', height: '40px' }}
                      />
                    </div>
                    <h2 className="mb-3 fw-bold">Welcome to Videomagic Promo</h2>
                    <p className="text-muted mb-4">
                      {sessionId ? 'Ready to create professional trailer clips' : 'Enter movie name to begin'}
                    </p>
                    
                    {sessionId && (
                      <div className="row g-3 justify-content-center">
                        {[
                          { text: 'Analyze this movie', icon: BarChart3 },
                          { text: 'Create 3 action clips for 15 seconds', icon: Zap },
                          { text: 'Give me one dramatic scene', icon: Film },
                          { text: 'Select comedy moments for 10 seconds each', icon: TrendingUp },
                          { text: 'Create a rollover video for this movie that can be used for an OTT platform', icon: Play }
                        ].map((action, idx) => (
                          <div key={idx} className="col-md-6 col-lg-4 col-xl-2 mb-3">
                            <div 
                              className="card border-primary cursor-pointer h-100"
                              onClick={async () => {
                                if (isLoading) return;
                                setInput(action.text);
                                
                                // Directly call the message sending logic
                                const userMsg = { role: 'user', content: action.text, timestamp: new Date() };
                                setMessages(prev => [...prev, userMsg]);
                                setInput('');
                                setIsLoading(true);

                                try {
                                  const response = await fetch(`${API_BASE}/api/chat/${sessionId}/message`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ message: action.text })
                                  });

                                  if (!response.ok) throw new Error('Failed to send message');
                                  const data = await response.json();
                                  
                                  if (data.state) {
                                    if (data.state.movie_analysis) {
                                      setMovieAnalysis(data.state.movie_analysis);
                                    }
                                    if (data.state.prompts) {
                                      setPrompts(data.state.prompts);
                                      const scenes = data.state.prompts.flatMap(p => p.scenes || []);
                                      setSelectedScenes(scenes);
                                    }
                                  }

                                  const lastMsg = data.messages[data.messages.length - 1];
                                  const assistantMsg = {
                                    role: 'assistant',
                                    content: lastMsg.content,
                                    timestamp: new Date(),
                                    messageType: lastMsg.additional_kwargs?.message_type,
                                    scenesData: lastMsg.additional_kwargs?.scenes_data,
                                    analysisData: lastMsg.additional_kwargs?.analysis_data
                                  };

                                  setMessages(prev => [...prev, assistantMsg]);

                                  if (assistantMsg.messageType === 'scene_selection') {
                                    setTimeout(() => setActiveTab('clips'), 500);
                                  } else if (assistantMsg.messageType === 'movie_analysis') {
                                    setTimeout(() => setActiveTab('analysis'), 500);
                                  }

                                } catch (error) {
                                  console.error('Error:', error);
                                  setMessages(prev => [...prev, {
                                    role: 'assistant',
                                    content: 'Error processing request. Please try again.',
                                    timestamp: new Date(),
                                    isError: true
                                  }]);
                                } finally {
                                  setIsLoading(false);
                                }
                              }}
                              style={{cursor: 'pointer', opacity: isLoading ? 0.6 : 1}}
                            >
                              <div className="card-body text-center p-4 d-flex flex-column justify-content-center align-items-center">
                                {isLoading ? (
                                  <>
                                    <div className="d-flex align-items-center justify-content-center mb-3" style={{height: '40px'}}>
                                      <div className="spinner-border text-primary"></div>
                                    </div>
                                    <h6 className="fw-semibold">Processing...</h6>
                                    <small className="text-muted">Please wait</small>
                                  </>
                                ) : (
                                  <>
                                    <div className="d-flex align-items-center justify-content-center mb-3" style={{height: '40px'}}>
                                      <action.icon size={32} className="text-primary" />
                                    </div>
                                    <h6 className="fw-semibold">{action.text}</h6>
                                    <small className="text-muted">Click to use</small>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {messages.map((msg, idx) => (
                      <div key={idx} className={`mb-4 d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                        <div className={`rounded-3 shadow-sm ${
                          msg.role === 'user' 
                            ? 'bg-primary text-white' 
                            : msg.isError
                              ? 'bg-danger text-white'
                              : 'bg-white border'
                        }`} style={{maxWidth: '75%'}}>
                          
                          <div className="p-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <div className="d-flex align-items-center gap-2">
                                <strong>{msg.role === 'user' ? 'You' : 'Assistant'}</strong>
                                {msg.messageType && (
                                  <span className={`badge ${
                                    msg.role === 'user' ? 'bg-white text-primary' : 'bg-light text-dark'
                                  } small`}>
                                    {msg.messageType.replace('_', ' ')}
                                  </span>
                                )}
                              </div>
                              <small className={`${msg.role === 'user' ? 'text-white-50' : 'text-muted'}`}>
                                {new Date(msg.timestamp).toLocaleTimeString()}
                              </small>
                            </div>
                            
                            <div style={{whiteSpace: 'pre-wrap'}}>{msg.content}</div>
                            
                            {msg.scenesData && (
                              <div className="mt-3 pt-3 border-top">
                                <small className="fw-medium">
                                  ✅ Generated {msg.scenesData.length} clips
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {isLoading && (
                      <div className="d-flex justify-content-start mb-4">
                        <div className="bg-white border rounded-3 p-4">
                          <div className="d-flex align-items-center">
                            <div className="spinner-border spinner-border-sm text-primary me-3"></div>
                            <div>
                              <div className="fw-medium">Processing...</div>
                              <small className="text-muted">Analyzing your request</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>
              
              {/* Input Area - Fixed */}
              {!showModal && !showDeleteConfirm && (
                <div className="border-top bg-white p-4">
                  <div className="input-group input-group-lg">
                  <input
                    ref={inputRef}
                    type="text"
                    className="form-control"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={sessionId ? "Ask me to analyze or create clips..." : "Movie Name | Video URL (optional)"}
                    disabled={isLoading}
                    autoComplete="off"
                  />
                  <button 
                    className="btn btn-primary px-4" 
                    onClick={sendMessage}
                    disabled={isLoading || !input.trim()}
                  >
                    {isLoading ? (
                      <div className="spinner-border spinner-border-sm"></div>
                    ) : (
                      <Send size={20} />
                    )}
                  </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Clips Tab */}
          {activeTab === 'clips' && (
            <div className="overflow-auto p-4" style={{backgroundColor: '#fafbfc'}}>
              {prompts.length > 0 ? (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h2 className="mb-1 fw-bold">Generated Clips</h2>
                      <p className="text-muted">Complete clip generation history with system data</p>
                    </div>
                    <div className="d-flex gap-2">
                      <span className="badge bg-success px-3 py-2">
                        {selectedScenes.filter(s => s.clipUrl).length} Ready
                      </span>
                      <span className="badge bg-danger px-3 py-2">
                        {selectedScenes.filter(s => !s.clipUrl).length} Failed
                      </span>
                    </div>
                  </div>
                  
                  {prompts.map((prompt, promptIdx) => (
                    <div 
                      key={promptIdx} 
                      className="card mb-4 shadow border-0"
                      ref={promptIdx === prompts.length - 1 ? lastPromptRef : null}
                    >
                      <div 
                        className="card-header bg-light border-0 cursor-pointer"
                        onClick={() => togglePromptExpansion(promptIdx)}
                        style={{cursor: 'pointer'}}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h5 className="mb-1 fw-bold d-flex align-items-center">
                              <span className="me-2">Prompt {promptIdx + 1}</span>
                            </h5>
                            {prompt.userQuery && (
                              <p className="text-muted small mb-0">"{prompt.userQuery}"</p>
                            )}
                          </div>
                          <div className="d-flex gap-2 align-items-center">
                            {prompt.scenes && (
                              <span className="badge bg-info">{prompt.scenes.length} scenes</span>
                            )}
                            {prompt.clipGeneration && (
                              <span className={`badge ${
                                prompt.clipGeneration.status === 'completed' ? 'bg-success' : 'bg-danger'
                              }`}>
                                {prompt.clipGeneration.status}
                              </span>
                            )}
                            <div 
                              className="text-muted" 
                              style={{
                                transform: expandedPrompts[promptIdx] ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease'
                              }}
                            >
                              <ChevronDown size={16} />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {expandedPrompts[promptIdx] && (
                        <div className="card-body">
                          {/* Processing Stats */}
                          {prompt.clipGeneration && (
                          <div className="mb-4">
                            <h6 className="fw-bold mb-3">Processing Statistics</h6>
                            <div className="row g-3">
                              <div className="col-md-4">
                                <div className="text-center p-3 bg-success bg-opacity-10 rounded">
                                  <div className="h4 fw-bold text-success">
                                    {prompt.clipGeneration.successful_clips || 0}
                                  </div>
                                  <small className="text-success">Successful</small>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="text-center p-3 bg-danger bg-opacity-10 rounded">
                                  <div className="h4 fw-bold text-danger">
                                    {prompt.scenes ? prompt.scenes.filter(scene => !scene.clipUrl).length : 0}
                                  </div>
                                  <small className="text-danger">Failed</small>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="text-center p-3 bg-info bg-opacity-10 rounded">
                                  <div className="h4 fw-bold text-info">
                                    {prompt.clipGeneration.stats?.processing_time_seconds || 0}s
                                  </div>
                                  <small className="text-info">Time</small>
                                </div>
                              </div>                            
                            </div>
                          </div>
                        )}
                        
                        {/* Generated Clips */}
                        {prompt.scenes && prompt.scenes.length > 0 && (
                          <div>
                            <h6 className="fw-bold mb-3">Generated Clips ({prompt.scenes.length})</h6>
                            <div className="row g-4">
                              {prompt.scenes.map((scene, sceneIdx) => (
                                <div key={sceneIdx} className="col-lg-4">
                                  <div className="card h-100 border-0 shadow">
                                    <div className="card-header bg-dark text-white">
                                      <div className="d-flex justify-content-between">
                                        <span className="fw-bold">Clip {scene.id}</span>
                                        <span className="badge bg-info">
                                          {scene.start_time} - {scene.end_time} ({scene.duration}s)
                                        </span>
                                      </div>
                                    </div>
                                    
                                    <div className="card-body p-0">
                                      {scene.clipUrl ? (
                                        <div className="position-relative">
                                          <video 
                                            className="w-100"
                                            controls
                                            style={{height: '200px', objectFit: 'cover'}}
                                          >
                                            <source src={scene.clipUrl} type="video/mp4" />
                                          </video>
                                          
                                          <div className="position-absolute top-0 end-0 p-2">
                                            <div className="btn-group">
                                              <button 
                                                className="btn btn-sm btn-light"
                                                onClick={() => navigator.clipboard.writeText(scene.clipUrl)}
                                                title="Copy URL"
                                              >
                                                <Copy size={14} />
                                              </button>
                                              <button 
                                                className="btn btn-sm btn-light"
                                                onClick={() => window.open(scene.clipUrl, '_blank')}
                                                title="Download"
                                              >
                                                <Download size={14} />
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="text-center py-4 bg-light">
                                          <AlertTriangle className="text-warning mb-2" size={32} />
                                          <p className="text-muted">Clip generation failed</p>
                                        </div>
                                      )}
                                      
                                      <div className="p-3">
                                        <div className="mb-3">
                                          <h6 className="fw-bold text-success">Selection Reason</h6>
                                          <p className="small text-muted">{scene.reason}</p>
                                        </div>
                                        
                                        {scene.transcript && (
                                          <details className="mb-2">
                                            <summary className="fw-bold small text-primary cursor-pointer">
                                              <VolumeX size={14} className="me-1" />Transcript
                                            </summary>
                                            <div className="small mt-2 p-2 bg-light rounded">
                                              {scene.transcript}
                                            </div>
                                          </details>
                                        )}
                                        
                                        {scene.visualDescription && (
                                          <details className="mb-2">
                                            <summary className="fw-bold small text-info cursor-pointer">
                                              <Eye size={14} className="me-1" />Visual Description
                                            </summary>
                                            <div className="small mt-2 p-2 bg-light rounded">
                                              {scene.visualDescription}
                                            </div>
                                          </details>
                                        )}
                                        
                                        {scene.englishTranslation && (
                                          <details>
                                            <summary className="fw-bold small text-warning cursor-pointer">
                                              <Languages size={14} className="me-1" />Translation
                                            </summary>
                                            <div className="small mt-2 p-2 bg-light rounded">
                                              {scene.englishTranslation}
                                            </div>
                                          </details>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5">
                  <Video size={80} className="text-muted mb-4" />
                  <h3 className="fw-bold mb-3">No Clips Generated</h3>
                  <p className="text-muted mb-4">Ask me to create trailer clips</p>
                  <button 
                    className="btn btn-primary btn-lg"
                    onClick={async () => {
                      if (isLoading) return;
                      const message = 'Create 3 action clips for 15 seconds each';
                      setInput(message);
                      
                      // Directly call the message sending logic
                      const userMsg = { role: 'user', content: message, timestamp: new Date() };
                      setMessages(prev => [...prev, userMsg]);
                      setInput('');
                      setIsLoading(true);

                      try {
                        const response = await fetch(`${API_BASE}/api/chat/${sessionId}/message`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ message })
                        });

                        if (!response.ok) throw new Error('Failed to send message');
                        const data = await response.json();
                        
                        if (data.state) {
                          if (data.state.movie_analysis) {
                            setMovieAnalysis(data.state.movie_analysis);
                          }
                          if (data.state.prompts) {
                            setPrompts(data.state.prompts);
                            const scenes = data.state.prompts.flatMap(p => p.scenes || []);
                            setSelectedScenes(scenes);
                          }
                        }

                        const lastMsg = data.messages[data.messages.length - 1];
                        const assistantMsg = {
                          role: 'assistant',
                          content: lastMsg.content,
                          timestamp: new Date(),
                          messageType: lastMsg.additional_kwargs?.message_type,
                          scenesData: lastMsg.additional_kwargs?.scenes_data,
                          analysisData: lastMsg.additional_kwargs?.analysis_data
                        };

                        setMessages(prev => [...prev, assistantMsg]);

                      } catch (error) {
                        console.error('Error:', error);
                        setMessages(prev => [...prev, {
                          role: 'assistant',
                          content: 'Error processing request. Please try again.',
                          timestamp: new Date(),
                          isError: true
                        }]);
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2"></div>
                        Generating...
                      </>
                    ) : (
                      'Generate Clips'
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Analysis Tab */}
          {activeTab === 'analysis' && (
  <div className="overflow-auto p-4" style={{ backgroundColor: '#fafbfc' }}>
    {movieAnalysis ? (
      <div className="card shadow border-0">
        <div className="card-header bg-primary text-white py-4">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0 fw-bold">{movieAnalysis.movieName}</h2>
            
            {/* Source Files Links & PDF Export */}
            <div className="d-flex gap-3">
              <PDFExport movieAnalysis={movieAnalysis} />

              {movieAnalysis.sourceFiles && (
                <>
                  {movieAnalysis.sourceFiles.transcriptUrl && (
                    <a
                      href={movieAnalysis.sourceFiles.transcriptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-decoration-none small d-flex align-items-center"
                      style={{fontSize: '0.8rem'}}
                    >
                      <FileText size={12} className="me-1" />
                      View Transcript
                    </a>
                  )}

                  {movieAnalysis.sourceFiles.visualUrl && (
                    <a
                      href={movieAnalysis.sourceFiles.visualUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-decoration-none small d-flex align-items-center"
                      style={{fontSize: '0.8rem'}}
                    >
                      <Eye size={12} className="me-1" />
                      View Visual Description
                    </a>
                  )}
                </>
              )}
            </div>
          </div>
          
          <div className="mt-3">
            <h5 className="mb-2 text-white">Story Overview</h5>
            <p className="mb-3 text-white">{movieAnalysis.storyAnalysis?.storyOverview}</p>
            {movieAnalysis.storyAnalysis?.plotSummary && (
              <>
                <h5 className="mb-2 text-white">Plot Summary</h5>
                <p className="mb-0 text-white">{movieAnalysis.storyAnalysis.plotSummary}</p>
              </>
            )}
          </div>
          <p className="mb-0 text-white mt-3">Complete Movie Analysis</p>
        </div>

        <div className="card-body p-4">
          <div className="row g-4">
            <div className="col-lg-3">
              <div className="card h-100 border-primary">
                <div className="card-header bg-primary bg-opacity-10">
                  <h5 className="mb-0 text-primary">
                    <Film size={20} className="me-2" />Overview
                  </h5>
                </div>
                <div className="card-body">
                  <p><strong>Genre:</strong> {movieAnalysis.overview?.genre}</p>
                  <p><strong>Duration:</strong> {movieAnalysis.overview?.estimatedDuration}</p>
                  <p><strong>Setting:</strong> {movieAnalysis.overview?.setting}</p>
                  {movieAnalysis.overview?.subGenres && movieAnalysis.overview.subGenres.length > 0 && (
                    <div className="mt-3">
                      <div className="mb-2"><strong>Subgenres:</strong></div>
                      <div className="d-flex flex-wrap gap-1">
                        {movieAnalysis.overview.subGenres.map((subgenre, i) => (
                          <span key={i} className="badge bg-primary">{subgenre}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-3">
              <div className="card h-100 border-info">
                <div className="card-header bg-info bg-opacity-10">
                  <h5 className="mb-0 text-info">
                    <Languages size={20} className="me-2" />Languages
                  </h5>
                </div>
                <div className="card-body">
                  <p><strong>Primary:</strong> {movieAnalysis.languageAnalysis?.primaryLanguage}</p>
                  {movieAnalysis.languageAnalysis?.languages && movieAnalysis.languageAnalysis.languages.length > 0 && (
                    <div style={{ height: '200px', marginTop: '15px' }}>
                      <Pie
                        data={{
                          labels: movieAnalysis.languageAnalysis.languages.map(lang => lang.language),
                          datasets: [{
                            data: movieAnalysis.languageAnalysis.languages.map(lang => lang.percentage),
                            backgroundColor: [
                              '#0dcaf0',
                              '#6f42c1',
                              '#fd7e14',
                              '#198754',
                              '#dc3545',
                              '#ffc107'
                            ],
                            borderWidth: 2,
                            borderColor: '#fff'
                          }]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'bottom',
                              labels: {
                                boxWidth: 12,
                                padding: 10,
                                font: {
                                  size: 11
                                }
                              }
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return context.label + ': ' + context.parsed + '%';
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-3">
              <div className="card h-100 border-warning">
                <div className="card-header bg-warning bg-opacity-10">
                  <h5 className="mb-0 text-warning">
                    <Users size={20} className="me-2" />Key Characters
                  </h5>
                </div>
                <div className="card-body">
                  {movieAnalysis.keyCharacters && movieAnalysis.keyCharacters.length > 0 ? (
                    movieAnalysis.keyCharacters.map((character, i) => (
                      <div key={i} className="mb-3 pb-2 border-bottom">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <strong className="text-capitalize">{character.name}</strong>
                          <div className="text-center">
                            <div className="badge bg-warning">{character.importance}/10</div>
                            <div className="small text-muted">importance</div>
                          </div>
                        </div>
                        <div className="small text-muted mb-1">
                          <strong>Role:</strong> {character.role.charAt(0).toUpperCase() + character.role.slice(1)}
                        </div>
                        <div className="small">{character.description}</div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted">No character data available</p>
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-3">
              <div className="card h-100 border-success">
                <div className="card-header bg-success bg-opacity-10">
                  <h5 className="mb-0 text-success">
                    <Target size={20} className="me-2" />Marketing
                  </h5>
                </div>
                <div className="card-body">
                  <p><strong>Type:</strong> {movieAnalysis.marketingInsights?.suggestedPromoType?.charAt(0).toUpperCase() + movieAnalysis.marketingInsights?.suggestedPromoType?.slice(1)}</p>
                  <p><strong>Audience:</strong> {movieAnalysis.marketingInsights?.targetAudience}</p>
                  <p><strong>Trailer:</strong> {movieAnalysis.marketingInsights?.trailerLength?.charAt(0).toUpperCase() + movieAnalysis.marketingInsights?.trailerLength?.slice(1)}</p>
                </div>
              </div>
            </div>
          </div>

          {movieAnalysis.toneAndMood && (
            <div className="row g-4 mt-3">
              <div className="col-12">
                <div className="card border-secondary">
                  <div className="card-header bg-secondary bg-opacity-10">
                    <h5 className="mb-0 text-secondary">
                      <Activity size={20} className="me-2" />Tone & Mood
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="text-center p-3 bg-secondary bg-opacity-10 rounded h-100 d-flex flex-column justify-content-center">
                          <div className="h6 fw-bold text-secondary mb-2">Primary Tone</div>
                          <div className="badge bg-secondary fs-6">{movieAnalysis.toneAndMood.primaryTone}</div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="text-center p-3 bg-secondary bg-opacity-10 rounded h-100 d-flex flex-column justify-content-center">
                          <div className="h6 fw-bold text-secondary mb-2">Intensity Level</div>
                          <div className="badge bg-secondary fs-6 text-capitalize">{movieAnalysis.toneAndMood.intensityLevel}</div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="p-3 bg-secondary bg-opacity-10 rounded h-100 d-flex flex-column">
                          <div className="h6 fw-bold text-secondary mb-2">Mood Progression</div>
                          <div className="small flex-grow-1">{movieAnalysis.toneAndMood.moodProgression}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {movieAnalysis.storyAnalysis && (
            <div className="row g-4 mt-3">
              <div className="col-12">
                <div className="card border-dark">
                  <div className="card-header bg-dark bg-opacity-10">
                    <h5 className="mb-0 text-dark">
                      <Book size={20} className="me-2" />Story Analysis
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="p-3 bg-dark bg-opacity-10 rounded h-100">
                          <div className="h6 fw-bold text-dark mb-2">Plot Summary</div>
                          <p className="small">{movieAnalysis.storyAnalysis.plotSummary}</p>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="p-3 bg-dark bg-opacity-10 rounded h-100">
                          <div className="h6 fw-bold text-dark mb-2">Main Conflict</div>
                          <p className="small">{movieAnalysis.storyAnalysis.mainConflict}</p>
                          <div className="h6 fw-bold text-dark mt-3 mb-2">Themes</div>
                          <div className="d-flex flex-wrap gap-1">
                            {movieAnalysis.storyAnalysis.themes.map((theme, i) => (
                              <span key={i} className="badge bg-dark">{theme}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="p-3 bg-dark bg-opacity-10 rounded">
                          <div className="h6 fw-bold text-dark mb-2">Act Structure</div>
                          <div className="row g-2">
                            {Object.entries(movieAnalysis.storyAnalysis.actStructure).map(([act, description], i) => (
                              <div key={i} className="col-md-4">
                                <div className="small">
                                  <strong>{act.charAt(0).toUpperCase() + act.slice(1)}:</strong> {description}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="p-3 bg-dark bg-opacity-10 rounded h-100">
                          <div className="h6 fw-bold text-dark mb-2">Key Story Beats</div>
                          {movieAnalysis.storyAnalysis.keyStoryBeats.map((beat, i) => (
                            <div key={i} className="mb-2">
                              <div className="small"><strong>{beat.beat}:</strong> {beat.significance}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="p-3 bg-dark bg-opacity-10 rounded h-100">
                          <div className="h6 fw-bold text-dark mb-2">Emotional Arcs</div>
                          {movieAnalysis.storyAnalysis.emotionalArcs.map((arc, i) => (
                            <div key={i} className="mb-2">
                              <div className="small"><strong>{arc.character}:</strong> {arc.arc}</div>
                            </div>
                          ))}
                          <div className="h6 fw-bold text-dark mt-3 mb-2">Plot Twists</div>
                          {movieAnalysis.storyAnalysis.plotTwists.map((twist, i) => (
                            <div key={i} className="small mb-1">{twist}</div>
                          ))}
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="p-3 bg-dark bg-opacity-10 rounded">
                          <div className="h6 fw-bold text-dark mb-2">Climax & Resolution</div>
                          <div className="small mb-2"><strong>Climax:</strong> {movieAnalysis.storyAnalysis.climax}</div>
                          <div className="small"><strong>Resolution:</strong> {movieAnalysis.storyAnalysis.resolution}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    ) : (
      <div className="text-center py-5">
        <BarChart3 size={80} className="text-muted mb-4" />
        <h3 className="fw-bold mb-3">No Analysis Available</h3>
        <p className="text-muted mb-4">Request movie analysis to see insights</p>
        <button
          className="btn btn-primary btn-lg"
          onClick={async () => {
            if (isLoading) return;
            const message = 'Analyze this movie';
            setInput(message);
            
            // Directly call the message sending logic
            const userMsg = { role: 'user', content: message, timestamp: new Date() };
            setMessages(prev => [...prev, userMsg]);
            setInput('');
            setIsLoading(true);

            try {
              const response = await fetch(`${API_BASE}/api/chat/${sessionId}/message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message })
              });

              if (!response.ok) throw new Error('Failed to send message');
              const data = await response.json();
              
              if (data.state) {
                if (data.state.movie_analysis) {
                  setMovieAnalysis(data.state.movie_analysis);
                }
                if (data.state.prompts) {
                  setPrompts(data.state.prompts);
                  const scenes = data.state.prompts.flatMap(p => p.scenes || []);
                  setSelectedScenes(scenes);
                }
              }

              const lastMsg = data.messages[data.messages.length - 1];
              const assistantMsg = {
                role: 'assistant',
                content: lastMsg.content,
                timestamp: new Date(),
                messageType: lastMsg.additional_kwargs?.message_type,
                scenesData: lastMsg.additional_kwargs?.scenes_data,
                analysisData: lastMsg.additional_kwargs?.analysis_data
              };

              setMessages(prev => [...prev, assistantMsg]);

            } catch (error) {
              console.error('Error:', error);
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Error processing request. Please try again.',
                timestamp: new Date(),
                isError: true
              }]);
            } finally {
              setIsLoading(false);
            }
          }}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="spinner-border spinner-border-sm me-2"></div>
              Analyzing...
            </>
          ) : (
            <>
              <BarChart3 size={20} className="me-2" />
              Analyze Movie
            </>
          )}
        </button>
      </div>
    )}
  </div>
)}
        </div>
      </div>
    </div>
    </>
  );
};

export default VideomagicPromo;